import Phaser from 'phaser';
import { isTouchDevice } from '../utils/DeviceUtils';

// === GESTIONNAIRE D'INPUT TACTILE ===
// Couche d'abstraction qui traduit les gestes tactiles en events
// compréhensibles par le jeu. Ne fait RIEN sur desktop.
//
// Gestes supportés :
// - Tap simple      → pointerdown standard (déjà géré par Phaser)
// - Double-tap      → event 'doubletap' sur la scène
// - Long press      → event 'longpress' sur la scène (avec position)
// - Swipe           → event 'swipe' sur la scène (direction)

/** Configuration des seuils de détection */
const LONG_PRESS_DELAY = 500;     // ms avant déclenchement long press
const DOUBLE_TAP_DELAY = 300;     // ms max entre deux taps
const SWIPE_MIN_DISTANCE = 40;    // px minimum pour un swipe
const SWIPE_MAX_TIME = 400;       // ms max pour un swipe
const LONG_PRESS_MOVE_TOLERANCE = 10; // px de tolérance de mouvement pendant long press

export type SwipeDirection = 'up' | 'down' | 'left' | 'right';

export interface LongPressEvent {
  x: number;
  y: number;
  worldX: number;
  worldY: number;
}

export interface DoubleTapEvent {
  x: number;
  y: number;
  worldX: number;
  worldY: number;
}

export interface SwipeEvent {
  direction: SwipeDirection;
  distance: number;
  startX: number;
  startY: number;
}

export class TouchInputManager {
  private scene: Phaser.Scene;
  private enabled: boolean;

  // État interne pour la détection de gestes
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private lastTapTime = 0;
  private lastTapX = 0;
  private lastTapY = 0;
  private pointerStartX = 0;
  private pointerStartY = 0;
  private pointerStartTime = 0;
  private isLongPressing = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.enabled = isTouchDevice();

    if (this.enabled) {
      this.setupListeners();
    }
  }

  /** Initialise les listeners sur la scène */
  private setupListeners(): void {
    const input = this.scene.input;

    input.on('pointerdown', this.onPointerDown, this);
    input.on('pointermove', this.onPointerMove, this);
    input.on('pointerup', this.onPointerUp, this);
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    this.pointerStartX = pointer.x;
    this.pointerStartY = pointer.y;
    this.pointerStartTime = Date.now();
    this.isLongPressing = false;

    // Démarrer le timer long press
    this.clearLongPressTimer();
    this.longPressTimer = setTimeout(() => {
      // Vérifier qu'on n'a pas trop bougé
      this.isLongPressing = true;
      this.scene.events.emit('longpress', {
        x: pointer.x,
        y: pointer.y,
        worldX: pointer.worldX,
        worldY: pointer.worldY,
      } as LongPressEvent);
    }, LONG_PRESS_DELAY);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.longPressTimer) return;

    // Si le doigt bouge trop pendant le long press, on annule
    const dx = pointer.x - this.pointerStartX;
    const dy = pointer.y - this.pointerStartY;
    if (Math.abs(dx) > LONG_PRESS_MOVE_TOLERANCE || Math.abs(dy) > LONG_PRESS_MOVE_TOLERANCE) {
      this.clearLongPressTimer();
    }
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {
    this.clearLongPressTimer();

    // Si c'était un long press, ne pas traiter comme tap/swipe
    if (this.isLongPressing) {
      this.isLongPressing = false;
      return;
    }

    const elapsed = Date.now() - this.pointerStartTime;
    const dx = pointer.x - this.pointerStartX;
    const dy = pointer.y - this.pointerStartY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Détection swipe
    if (dist >= SWIPE_MIN_DISTANCE && elapsed <= SWIPE_MAX_TIME) {
      const direction: SwipeDirection =
        Math.abs(dx) > Math.abs(dy)
          ? (dx > 0 ? 'right' : 'left')
          : (dy > 0 ? 'down' : 'up');

      this.scene.events.emit('swipe', {
        direction,
        distance: dist,
        startX: this.pointerStartX,
        startY: this.pointerStartY,
      } as SwipeEvent);

      // Un swipe n'est pas un tap
      this.lastTapTime = 0;
      return;
    }

    // Détection double-tap (seulement si c'est un tap court sans déplacement)
    if (dist < SWIPE_MIN_DISTANCE && elapsed < SWIPE_MAX_TIME) {
      const now = Date.now();
      const tapDx = Math.abs(pointer.x - this.lastTapX);
      const tapDy = Math.abs(pointer.y - this.lastTapY);

      if (now - this.lastTapTime < DOUBLE_TAP_DELAY && tapDx < 30 && tapDy < 30) {
        this.scene.events.emit('doubletap', {
          x: pointer.x,
          y: pointer.y,
          worldX: pointer.worldX,
          worldY: pointer.worldY,
        } as DoubleTapEvent);
        this.lastTapTime = 0; // Reset pour éviter triple-tap
      } else {
        this.lastTapTime = now;
        this.lastTapX = pointer.x;
        this.lastTapY = pointer.y;
      }
    }
  }

  private clearLongPressTimer(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  /** Nettoyer les listeners (appeler dans scene.shutdown) */
  destroy(): void {
    if (!this.enabled) return;

    this.clearLongPressTimer();

    const input = this.scene.input;
    input.off('pointerdown', this.onPointerDown, this);
    input.off('pointermove', this.onPointerMove, this);
    input.off('pointerup', this.onPointerUp, this);
  }

  /** Indique si le gestionnaire tactile est actif */
  isActive(): boolean {
    return this.enabled;
  }
}
